import React, { useEffect, useMemo, useState } from "react";
import { Plus, X, Check, Layers } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import VaultCard from "../cards/VaultCard";

/**
 * FavoriteCardsBinder — picker (edit mode) + read-only strip (display mode)
 * for up to 5 Vault Cards the user wants to feature on their public profile.
 *
 * Owner view (/profile): renders the strip with an "edit" toggle that pops
 *   a 6-col grid of their cards; click to toggle into/out of the favorites
 *   list (max 5). Saves on every toggle via PUT /me/favorite-cards.
 * Visitor view (/u/:username): pure read-only strip — uses `cards` prop only.
 */
export default function FavoriteCardsBinder({ cards = [], editable = false, onChanged = null }) {
  const [editing, setEditing] = useState(false);
  const [favIds, setFavIds] = useState(() => cards.map((c) => c.id));
  const [allMine, setAllMine] = useState(null);
  const [busy, setBusy] = useState(false);

  // Keep favIds in sync if parent re-fetches.
  useEffect(() => { setFavIds(cards.map((c) => c.id)); }, [cards]);

  // Fetch the user's full collection lazily — only the FIRST time edit mode opens.
  useEffect(() => {
    if (!editing || allMine !== null) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/vault-cards/me");
        if (alive) setAllMine(data?.items || []);
      } catch {
        if (alive) setAllMine([]);
      }
    })();
    return () => { alive = false; };
  }, [editing, allMine]);

  const toggle = async (cardId) => {
    if (busy) return;
    let next;
    if (favIds.includes(cardId)) {
      next = favIds.filter((x) => x !== cardId);
    } else if (favIds.length >= 5) {
      toast.error("Max 5 favorite cards — remove one first");
      return;
    } else {
      next = [...favIds, cardId];
    }
    setBusy(true);
    try {
      const { data } = await api.put("/me/favorite-cards", { card_ids: next });
      setFavIds(data.favorite_card_ids || next);
      if (onChanged) onChanged(data.favorite_card_ids || next);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const orderedFavCards = useMemo(() => {
    // Render cards in favIds order, falling back to the catalog rows we
    // received in props.
    const byId = new Map();
    [...(cards || []), ...((allMine || []).map((r) => r.card))].forEach((c) => {
      if (c?.id) byId.set(c.id, c);
    });
    return favIds.map((id) => byId.get(id)).filter(Boolean);
  }, [favIds, cards, allMine]);

  if (!editable && orderedFavCards.length === 0) {
    return null; // public profile with no favorites — section is hidden
  }

  return (
    <div data-testid="favorite-cards-binder">
      {/* Read-only strip — fluid image previews so all 5 always fit inside
          the parent box on any width. Falls back to a designed name plate
          if the artwork URL is missing.
          Owner edit mode adds an X overlay so the owner can ALWAYS unpin
          directly from the strip — critical defense against pinned cards
          that no longer exist in the user's inventory (would otherwise be
          stuck since the picker can only toggle cards the user still owns). */}
      {orderedFavCards.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2" data-testid="fav-cards-strip">
          {orderedFavCards.slice(0, 5).map((c) => {
            const art = c.art_url || `/api/vault-cards/${encodeURIComponent(c.id)}/artwork.png`;
            const rarityColor = ({
              mythic: "#ff3b30", legendary: "#ffd60a", epic: "#a78bfa",
              rare: "#22d3ee", uncommon: "#22c55e", common: "#94a3b8",
            })[c.rarity] || "#94a3b8";
            return (
              <div
                key={c.id}
                className="relative aspect-[2.5/3.5] bg-black overflow-hidden border min-w-0"
                style={{ borderColor: `${rarityColor}66` }}
                title={`${c.name || c.id} · ${c.rarity || ""}`}
              >
                <img
                  src={art}
                  alt={c.name || c.id}
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
                <div
                  className="absolute top-1 left-1 w-1.5 h-1.5 rounded-full"
                  style={{ background: rarityColor, boxShadow: `0 0 6px ${rarityColor}` }}
                />
                {editable && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); toggle(c.id); }}
                    disabled={busy}
                    data-testid={`fav-card-unpin-${c.id}`}
                    title="Unpin"
                    aria-label="Unpin card"
                    className="absolute top-1 right-1 bg-black/85 hover:bg-[#ff3b30] text-white hover:text-black p-1 disabled:opacity-50 transition-colors z-10"
                  >
                    <X size={11} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
      {/* Owner-only: edit toggle + picker */}
      {editable && (
        <>
          {orderedFavCards.length === 0 && !editing && (
            <div className="border border-dashed border-white/15 bg-[#0a0a0a] bg-gradient-to-br from-[#ff9500]/[0.08] to-[#ff9500]/[0.02] px-4 py-6 text-center">
              <Layers className="mx-auto text-[#ff9500]/70" size={20} />
              <p className="mt-2 font-mono text-[10px] tracking-[0.25em] uppercase text-[#ff9500]/80">
                // NO CARDS PINNED YET
              </p>
            </div>
          )}
          <button
            type="button"
            onClick={() => setEditing((v) => !v)}
            data-testid="fav-cards-edit-btn"
            className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#ff9500]/50 text-[#ff9500] hover:bg-[#ff9500]/10 hover:text-white font-mono text-[10px] tracking-[0.25em] uppercase transition-colors"
          >
            {editing ? <X size={11} /> : <Plus size={11} />}
            {editing ? "Done" : "Pick favorites"}
          </button>
          {editing && (
            <div className="mt-3 border border-[#ff9500]/30 p-3" style={{ background: "#050505" }}>
              <div className="font-mono text-[10px] tracking-[0.25em] text-zinc-500 mb-3 uppercase">
                // YOUR COLLECTION — tap up to 5 to feature
              </div>
              {allMine === null ? (
                <p className="text-zinc-600 font-mono text-[10px] tracking-[0.2em] uppercase">Loading…</p>
              ) : allMine.length === 0 ? (
                <p className="text-zinc-600 font-mono text-[10px] tracking-[0.2em] uppercase">
                  // YOU HAVEN'T PULLED ANY CARDS YET
                </p>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-6 gap-2 max-h-[420px] overflow-y-auto">
                  {allMine.map((row) => {
                    const c = row.card;
                    if (!c) return null;
                    const picked = favIds.includes(c.id);
                    return (
                      <button
                        key={c.id + (row.copy_number || "")}
                        type="button"
                        onClick={() => toggle(c.id)}
                        disabled={busy}
                        data-testid={`fav-pick-${c.id}`}
                        className={`relative aspect-[2.5/3.5] border transition-all ${picked ? "border-[#ff9500]" : "border-white/10 hover:border-white/30"} ${busy ? "opacity-60 cursor-wait" : "cursor-pointer"}`}
                        style={picked ? { boxShadow: "0 0 0 1px #ff9500, 0 0 22px -6px #ff9500" } : undefined}
                        title={c.name}
                      >
                        <VaultCard card={c} compact />
                        {picked && (
                          <span className="absolute top-1 right-1 bg-[#ff9500] text-[#050505] px-1 py-0.5 font-mono text-[9px] tracking-[0.15em] flex items-center gap-0.5">
                            <Check size={8} /> {favIds.indexOf(c.id) + 1}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
